import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/seo/SEO";
import { GoldButton, GhostButton, Eyebrow, SectionHeading, AmbientOrbs, GlassCard } from "@/components/common/ui";
import { UserCircle, LinkedinLogo, Buildings, Sparkle, ShieldCheck, Globe, Headset, Wrench, TrendUp, Handshake, Heart } from "@phosphor-icons/react";
import { VALUES, FOUNDERS, TEAM_DEPARTMENTS, TRUST_SIGNALS } from "@/data/company";

const SIGNAL_ICONS = {
  hoteliers: Buildings, privacy: ShieldCheck, security: ShieldCheck, ai: Sparkle,
  eu: Globe, support: Headset, dev: Wrench, vision: TrendUp,
};

export default function Company() {
  const { i18n, t } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";

  return (
    <>
      <SEO title={isNl ? "Bedrijf — BHT Solutions" : "Company — BHT Solutions"} path="/company" />

      {/* Hero */}
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{isNl ? "Wie we zijn" : "Who we are"}</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-5xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl"
          >
            {isNl ? "Gebouwd door hospitality. Ontworpen voor moderne hotels." : "Built by hospitality. Designed for modern hotels."}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">
            {isNl
              ? "BHT Solutions is een Europees hospitality-technologiebedrijf, ontstaan uit de dagelijkse realiteit van een hotel runnen. We bouwen het platform dat we zelf altijd hadden gewild."
              : "BHT Solutions is a European hospitality technology company, born from the daily reality of running a hotel. We're building the platform we always wished we had."}
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="relative border-y border-white/[0.06] py-24 lg:py-32" data-testid="company-story">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow={isNl ? "Ons verhaal" : "Our story"} title={isNl ? "Niet een softwarebedrijf dat hospitality ontdekte." : "Not a software company that discovered hospitality."} />
          </div>
          <div className="space-y-6 text-base leading-relaxed text-white/70 lg:col-span-7 lg:text-lg">
            <p>
              {isNl
                ? "BHT One is niet ontstaan omdat een softwarebedrijf de hotelmarkt wilde betreden. Het is ontstaan omdat wij zelf de operationele frustraties hebben ervaren van een hotel runnen — dagelijks, jarenlang."
                : "BHT One wasn't created because a software company wanted to enter hospitality. It was created because we personally experienced the operational frustrations of running a hotel — daily, for years."}
            </p>
            <p>
              {isNl
                ? "We werkten met vijf verschillende systemen die geen van allen met elkaar spraken. Housekeeping en receptie belden elkaar over kamers. Kennis over gastvoorkeuren zat in het hoofd van één medewerker. Nieuwe collega's waren wekenlang niet productief. Managers puzzelden rapporten uit vier leveranciers bij elkaar — altijd een dag te laat."
                : "We ran five different systems that didn't talk to each other. Housekeeping and reception phoned each other about rooms. Knowledge about guest preferences lived in one employee's head. New colleagues weren't productive for weeks. Managers pieced together reports from four vendors — always a day too late."}
            </p>
            <p>
              {isNl
                ? "Elke workflow in BHT One begon als een écht probleem dat een échte hotelier wilde oplossen. Technologie volgt hospitality — niet andersom. Dat is de reden waarom hoteliers ons tijdens hun eerste demo hetzelfde vertellen:"
                : "Every workflow in BHT One started as a real problem a real hotelier wanted to solve. Technology follows hospitality — never the other way around. That's why hoteliers tell us the same thing on their first demo:"}
            </p>
            <blockquote className="border-l-2 border-bht-gold pl-6 font-serif text-2xl italic leading-snug text-white">
              {isNl ? "'Jullie snappen precies hoe mijn hotel werkt.'" : "'You understand exactly how my hotel works.'"}
            </blockquote>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="relative py-24 lg:py-32" data-testid="company-founders">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={isNl ? "Founders" : "Founders"} title={isNl ? "De mensen achter BHT One." : "The people behind BHT One."} sub={isNl ? "Volledige profielen en portretten volgen binnenkort. Voor nu: de rollen." : "Full profiles and portraits are being prepared. For now: the roles."} />
          <div className="mt-16 grid gap-6 md:grid-cols-2">
            {FOUNDERS.map((f) => (
              <div key={f.id} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8" data-testid={`founder-${f.id}`}>
                <div className="flex items-start gap-6">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border border-bht-gold/40 bg-gradient-to-br from-bht-navy to-bht-void">
                    <UserCircle size={36} weight="thin" className="text-bht-gold" />
                  </div>
                  <div className="flex-1">
                    <div className="font-serif text-2xl text-white">{f.name}</div>
                    <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-bht-gold">{f.role[lang]}</div>
                    <p className="mt-4 text-sm leading-relaxed text-white/60">{f.bio[lang]}</p>
                    {f.linkedin && (
                      <a href={f.linkedin} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-xs text-white/70 hover:text-bht-gold">
                        <LinkedinLogo size={14} /> LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission + Vision */}
      <section className="relative border-y border-white/[0.06] bg-bht-navy2/30 py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-10 md:grid-cols-2">
            <GlassCard className="p-10">
              <Eyebrow>{isNl ? "Onze missie" : "Our mission"}</Eyebrow>
              <div className="mt-6 font-serif text-3xl leading-tight tracking-tight">
                {isNl
                  ? "Technologie moet hotelmedewerkers meer tijd geven voor écht gastheerschap."
                  : "Technology should give hotel employees more time for genuine hospitality."}
              </div>
            </GlassCard>
            <GlassCard className="p-10">
              <Eyebrow>{isNl ? "Onze visie" : "Our vision"}</Eyebrow>
              <div className="mt-6 font-serif text-3xl leading-tight tracking-tight">
                {isNl
                  ? "Bouw het meest complete Hospitality Operating System ter wereld — dat gastvrijheid ondersteunt, niet vervangt."
                  : "Build the world's most complete Hospitality Operating System — one that supports hospitality, never replaces it."}
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-24 lg:py-32" data-testid="company-values">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={isNl ? "Onze waarden" : "Our values"} title={isNl ? "Wat ons drijft." : "What drives us."} />
          <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <div key={v.key} className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-colors hover:border-bht-gold/30" data-testid={`value-${v.key}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">V/{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-5 font-serif text-xl">{v[lang]}</div>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{v.body[lang]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team departments (scalable) */}
      <section className="relative border-y border-white/[0.06] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={isNl ? "Team" : "Team"} title={isNl ? "We groeien met opzet." : "We're growing on purpose."} sub={isNl ? "Onze afdelingen — schaalbaar, LinkedIn-integratie later." : "Our departments — scalable, LinkedIn integration to follow."} />
          <div className="mt-12 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {TEAM_DEPARTMENTS.map((d) => (
              <div key={d.key} className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-4 text-center">
                <div className="font-serif text-lg text-white">{d[lang]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="relative py-24 lg:py-32" data-testid="company-trust">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={isNl ? "Wat u van ons kunt verwachten" : "What you can expect from us"} title={isNl ? "Signalen die ertoe doen." : "Signals that matter."} />
          <div className="mt-12 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
            {TRUST_SIGNALS.map((s, i) => {
              const Icon = SIGNAL_ICONS[s.icon] || Sparkle;
              return (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-4">
                  <Icon size={20} weight="thin" className="shrink-0 text-bht-gold" />
                  <span className="text-sm text-white/85">{s[lang]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-t border-white/[0.06] py-32">
        <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
          <SectionHeading align="center" eyebrow={t("brand.name")} title={isNl ? "Klaar om met ons te praten?" : "Ready to talk?"} sub={isNl ? "We reageren binnen één werkdag. Een hotelier uit ons team neemt contact met u op." : "We reply within one business day. A hotelier from our team will reach out."} />
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <GoldButton to={`/${lang}/book-demo`}>{t("cta.primary")}</GoldButton>
            <GhostButton to={`/${lang}/contact`}>{t("nav.contact")} →</GhostButton>
          </div>
        </div>
      </section>
    </>
  );
}
